/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable max-lines */

/**
 * backendのエラーコードをフロントエンドのエラーコードに変換する
 *
 * @param {string} viewCode 画面コード
 * @param {string} backendErrorCode バックエンドのエラーコード
 * @returns {string} フロントエンドのエラーコード
 */
export const convertToFrontendErrorCode = (
  viewCode: ViewCode,
  backendErrorCode: string
): string => {
  // S3APIのエラーコードは全て200101に変換する。
  // S3APIのエラーコードは9019xxxで定義されてる。
  if (backendErrorCode.startsWith('9019')) {
    return ErrorCode.COMMON_S3API_ERROR
  }
  // FIXME: バックエンドとフロントエンドのエラーコードは本来一対一にする必要はない。
  // ただ現状は一対一になってしまっているため、S3APIのように複数のバックエンドのエラーコードを
  // 一つのフロントエンドのエラーコードに変換する仕組みを追加する（R2.0以降のリファクタリング案件）
  return errorCodeMap[viewCode] === undefined ||
    // @ts-ignore
    errorCodeMap[viewCode][backendErrorCode] === undefined
    ? `21${viewCode}99`
    : // @ts-ignore
      errorCodeMap[viewCode][backendErrorCode]
}

export const MAP_ERROR_TITLE = '地図エラー'
export const CREATE_MAP_OFFLINE_DATA_ERROR =
  'オフラインのため地図情報を取得できませんでした。被災建物の編集は行えません。'
export const EDIT_MAP_OFFLINE_DATA_ERROR = `オフラインのため地図情報を取得できませんでした。
  通信のある環境でアクセスするか、オフライン地図をダウンロードしてください。`

// 調査完了complete
export const INSPECTION_COMPLETE_OPERATION_LOG_NETWORK_ERROR = convertToFrontendErrorCode

/**
 * 画面コード一覧
 */
export enum ViewCode {
  // 調査計画一覧画面
  INSPECTION_PLAN = '12'
}

/**
 * エラーコード一覧
 */
export enum ErrorCode {
  /** 共通 */
  // S3APIのエラー
  COMMON_S3API_ERROR = '210101', // S3APIのエラー
  // デバイス認証でエラー発生またはdeviceTokenがない
  INSPECTION_PLAN_DEVICE_AUTH_ERROR = '211253',
  // 後方支援側の設定でデバイス認証が不許可。
  INSPECTION_PLAN_DEVICE_AUTH_NO_PERMISSION_ERROR = '211254',

  /** 調査計画一覧画面 **/
  // [start] 行政区画API (adminDistricts)
  // 行政区画情報API呼び出しに関数が指定されていない
  INSPECTION_PLAN_ADMIN_DISTRICTS_NO_FUNCTION_PARAM = '211200',
  // 行政区画情報API呼び出しに引数が指定されていない
  INSPECTION_PLAN_ADMIN_DISTRICTS_NO_PARAMS = '211201',
  // 行政区画情報API呼び出しにサポート外の関数が指定された
  INSPECTION_PLAN_ADMIN_DISTRICTS_NO_SUPPORTED_FUNCTION = '211202',
  // 行政区画情報API呼び出しに自治体コード (govCode) が指定されていない
  INSPECTION_PLAN_ADMIN_DISTRICTS_NO_GOV_CODE = '211203',
  // 行政区画情報API呼び出しに区画の種別 (type) が指定されていない
  INSPECTION_PLAN_ADMIN_DISTRICTS_NO_TYPE = '211204',
  // 行政区画情報API呼び出しに住所コード (addressCode) が指定されていない
  INSPECTION_PLAN_ADMIN_DISTRICTS_NO_ADDRESS_CODE = '211205',
  // 行政区画情報API呼び出しに都道府県コード (prefCode) が指定されていない
  INSPECTION_PLAN_ADMIN_DISTRICTS_NO_PREF_CODE = '211206',
  // 行政区画情報API呼び出しに都道府県名 (prefName) が指定されていない
  INSPECTION_PLAN_ADMIN_DISTRICTS_NO_PREF_NAME = '211207',
  // 行政区画情報API呼び出しに市区町村コード (cityCode) が指定されていない
  INSPECTION_PLAN_ADMIN_DISTRICTS_NO_CITY_CODE = '211208',
  // 行政区画情報API呼び出しに市区町村名 (cityName) が指定されていない
  INSPECTION_PLAN_ADMIN_DISTRICTS_NO_CITY_NAME = '211209',
  // 行政区画情報API呼び出しに町丁目コード (streetCode) が指定されていない
  INSPECTION_PLAN_ADMIN_DISTRICTS_NO_STREET_CODE = '211210',
  // 行政区画情報API呼び出しに町丁・字名 (streetName) が指定されていない
  INSPECTION_PLAN_ADMIN_DISTRICTS_NO_STREET_NAME = '211211',
  // 行政区画APIのquery関数で不正なリクエスト引数の型が指定された
  INSPECTION_PLAN_ADMIN_DISTRICTS_QUERY_TYPE_ERROR = '211249',
  // [end] 行政区画API (adminDistricts)

  // [begin] 調査対象API (inv/inspectionTarget)
  // 調査対象APIのバリデーションエラー
  INSPECTION_PLAN_INV_INSPECTION_TARGET_VALIDATION_ERROR = '211212',
  // 調査対象APIのCORSエラー
  INSPECTION_PLAN_INV_INSPECTION_TARGET_CORS_ERROR = '211213',
  // 調査対象APIの実行権限エラー
  INSPECTION_PLAN_INV_INSPECTION_TARGET_NO_AUTH = '211214',
  // 調査対象APIのサポート外の関数が指定された
  INSPECTION_PLAN_INV_INSPECTION_TARGET_NO_SUPPORTED_FUNCTION = '211215',
  // 調査対象APIの外部API呼び出しでのエラー
  INSPECTION_PLAN_INV_INSPECTION_TARGET_API_ACCESS_ERROR = '211216',
  // 調査対象APIの対象日付が設定されていない
  INSPECTION_PLAN_INV_INSPECTION_TARGET_NO_TARGET_DATE = '211217',
  // 調査対象APIの調査班情報が存在しない
  INSPECTION_PLAN_INV_INSPECTION_TARGET_NO_TEAM_ID = '211218',
  // 調査対象APIの災害情報が存在しない
  INSPECTION_PLAN_INV_INSPECTION_TARGET_NO_DISASTER_ID = '211219',
  // 調査対象APIで想定外のエラーが発生した
  INSPECTION_PLAN_INV_INSPECTION_TARGET_UNKNOWN_ERROR = '211220',
  // 調査対象APIのquery関数で不正なリクエスト引数の型が指定された
  INSPECTION_PLAN_INV_INSPECTION_TARGET_QUERY_TYPE_ERROR = '211250',
  // [end] 調査対象API (inv/inspectionTarget)

  // [begin] 調査結果API (inspectionResult)
  // 調査結果APIのバリデーションエラー
  INSPECTION_PLAN_INSPECTION_RESULT_VALIDATION_ERROR = '211221',
  // 調査結果APIのCORSエラー
  INSPECTION_PLAN_INSPECTION_RESULT_CORS_ERROR = '211222',
  // 調査結果APIの実行権限エラー
  INSPECTION_PLAN_INSPECTION_RESULT_NO_AUTH = '211223',
  // 調査結果APIのサポート外の関数が指定された
  INSPECTION_PLAN_INSPECTION_RESULT_NO_SUPPORTED_FUNCTION = '211224',
  // 調査結果APIで存在しない調査IDが指定された (sendBack)
  INSPECTION_PLAN_INSPECTION_RESULT_NO_INSPECTION_ID_SEND_BACK = '211225',
  // 調査結果APIで存在しない調査IDが指定された (modify)
  INSPECTION_PLAN_INSPECTION_RESULT_NO_INSPECTION_ID_MODIFY = '211226',
  // 調査結果APIで無効なtypeが指定された
  INSPECTION_PLAN_INSPECTION_RESULT_INVALID_TYPE = '211227',
  // 調査結果APIで想定外なエラーが発生した
  INSPECTION_PLAN_INSPECTION_RESULT_UNKNOWN_ERROR = '211228',
  // 調査結果APIのget関数で不正なリクエスト引数の型が指定された
  INSPECTION_PLAN_INSPECTION_RESULT_GET_TYPE_ERROR = '211251',
  // [end] 調査結果API (inspectionResult)

  // [begin] 調査計画API (operation/inspectionTarget)
  // 調査対象APIのバリデーションエラー
  INSPECTION_PLAN_OPE_INSPECTION_TARGET_VALIDATION_ERROR = '211229',
  // 調査対象APIのCORSエラー
  INSPECTION_PLAN_OPE_INSPECTION_TARGET_CORS_ERROR = '211230',
  // 調査結果APIの実行権限エラー
  INSPECTION_PLAN_OPE_INSPECTION_TARGET_NO_AUTH = '211231',
  // 調査結果APIのサポート外の関数が指定された
  INSPECTION_PLAN_OPE_INSPECTION_TARGET_NO_SUPPORTED_FUNCTION = '211232',
  // 調査結果APIのget_itemでエラーが発生
  INSPECTION_PLAN_OPE_INSPECTION_TARGET_FAILED_GET_ITEM = '211233',
  // 調査結果APIでエラーメッセージ定義ファイルの読み込みに失敗
  INSPECTION_PLAN_OPE_INSPECTION_TARGET_FAILED_READ_EXCEPTION_JSON = '211234',
  // 調査結果APIで想定外なエラーが発生した
  INSPECTION_PLAN_OPE_INSPECTION_TARGET_UNKNOWN_ERROR = '211235',
  // [end] 調査計画API (operation/inspectionTarget)

  // [begin] 申し送り事項API (messageForSuccessor)
  // 申し送り事項APIのバリデーションエラー
  INSPECTION_PLAN_MESSAGE_FOR_SUCCESSOR_VALIDATION_ERROR = '211236',
  // 申し送り事項APIのCORSエラー
  INSPECTION_PLAN_MESSAGE_FOR_SUCCESSOR_CORS_ERROR = '211237',
  // 申し送り事項APIのget権限エラー
  INSPECTION_PLAN_MESSAGE_FOR_SUCCESSOR_NO_AUTH = '211238',
  // 申し送り事項APIでサポート外の関数が指定された
  INSPECTION_PLAN_MESSAGE_FOR_SUCCESSOR_NO_SUPPORTED_FUNCTION = '211239',
  // 申し送り事項APIで想定外のエラーが発生した
  INSPECTION_PLAN_MESSAGE_FOR_SUCCESSOR_UNKNOWN_ERROR = '211240',
  // [end] 申し送り事項API (messageForSuccessor)

  // [begin] 申請情報API (applications)
  // 申請情報APIのバリデーションエラー
  INSPECTION_PLAN_APPLICATIONS_VALIDATION_ERROR = '211241',
  // 申請情報APIのCORSエラー
  INSPECTION_PLAN_APPLICATIONS_CORS_ERROR = '211242',
  // 申請情報APIの実行権限エラー
  INSPECTION_PLAN_APPLICATIONS_NO_AUTH = '211243',
  // 申請情報APIのサポート外の関数が指定された
  INSPECTION_PLAN_APPLICATIONS_NO_SUPPORTED_FUNCTION = '211244',
  // 申請情報APIのcount関数でエラー
  INSPECTION_PLAN_APPLICATIONS_FAILED_COUNT = '211245',
  // 申請情報APIでエラーメッセージ定義ファイルの読み込みに失敗
  INSPECTION_PLAN_APPLICATIONS_FAILED_READ_EXCEPTION_JSON = '211246',
  // 申請情報APIで想定外のエラーが発生した
  INSPECTION_PLAN_APPLICATIONS_UNKNOWN_ERROR = '211247',
  // 申請情報APIのcount関数で不正なリクエスト引数の型が指定された
  INSPECTION_PLAN_APPLICATIONS_COUNT_TYPE_ERROR = '211248',
  // 申請情報APIのqueryByHousehold関数で不正なリクエスト引数の型が指定された
  INSPECTION_PLAN_APPLICATIONS_QUERY_BY_HOUSEHOLD_TYPE_ERROR = '211255'
  // [end] 申請情報API (applications)
}

/**
 * 画面コード: { BEエラーコード: FEエラーコード }
 */
const errorCodeMap = {
  // 調査計画一覧画面
  12: {
    /* 行政区画情報 */
    // 関数が設定されていない
    901101: ErrorCode.INSPECTION_PLAN_ADMIN_DISTRICTS_NO_FUNCTION_PARAM,
    // 引数が設定されていない
    901102: ErrorCode.INSPECTION_PLAN_ADMIN_DISTRICTS_NO_PARAMS,
    // サポート外の関数が指定された
    901103: ErrorCode.INSPECTION_PLAN_ADMIN_DISTRICTS_NO_SUPPORTED_FUNCTION,
    // 自治体コード (govCode) が設定されていない
    901104: ErrorCode.INSPECTION_PLAN_ADMIN_DISTRICTS_NO_GOV_CODE,
    // 区画の種別 (type) が指定されていない
    901105: ErrorCode.INSPECTION_PLAN_ADMIN_DISTRICTS_NO_TYPE,
    // 住所コード (addressCode) が指定されていない
    901106: ErrorCode.INSPECTION_PLAN_ADMIN_DISTRICTS_NO_ADDRESS_CODE,
    // 都道府県コード (prefCode) が指定されていない
    901107: ErrorCode.INSPECTION_PLAN_ADMIN_DISTRICTS_NO_PREF_CODE,
    // 都道府県名 (prefName) が指定されていない
    901108: ErrorCode.INSPECTION_PLAN_ADMIN_DISTRICTS_NO_PREF_NAME,
    // 市区町村コード (cityCode) が指定されていない
    901109: ErrorCode.INSPECTION_PLAN_ADMIN_DISTRICTS_NO_CITY_CODE,
    // 市区町村名 (cityName) が指定されていない
    901110: ErrorCode.INSPECTION_PLAN_ADMIN_DISTRICTS_NO_CITY_NAME,
    // 町丁目コード (streetCode) が設定されていない
    901111: ErrorCode.INSPECTION_PLAN_ADMIN_DISTRICTS_NO_STREET_CODE,
    // 町丁・字名 (streetName) が指定されていない
    901112: ErrorCode.INSPECTION_PLAN_ADMIN_DISTRICTS_NO_STREET_NAME,
    /* 調査対象API */
    // 調査対象APIのバリデーションエラー
    201200: ErrorCode.INSPECTION_PLAN_INV_INSPECTION_TARGET_VALIDATION_ERROR,
    // 調査対象APIのCORSエラー
    201201: ErrorCode.INSPECTION_PLAN_INV_INSPECTION_TARGET_CORS_ERROR,
    // 調査対象APIの実行権限エラー
    201202: ErrorCode.INSPECTION_PLAN_INV_INSPECTION_TARGET_NO_AUTH,
    // 調査対象APIのサポート外の関数が指定された
    201203: ErrorCode.INSPECTION_PLAN_INV_INSPECTION_TARGET_NO_SUPPORTED_FUNCTION,
    // 調査対象APIの外部API呼び出しでのエラー
    201210: ErrorCode.INSPECTION_PLAN_INV_INSPECTION_TARGET_API_ACCESS_ERROR,
    // 調査対象APIの対象日付が設定されていない
    201211: ErrorCode.INSPECTION_PLAN_INV_INSPECTION_TARGET_NO_TARGET_DATE,
    // 調査対象APIの調査班情報が存在しない
    201212: ErrorCode.INSPECTION_PLAN_INV_INSPECTION_TARGET_NO_TEAM_ID,
    // 調査対象APIの災害情報が存在しない
    201213: ErrorCode.INSPECTION_PLAN_INV_INSPECTION_TARGET_NO_DISASTER_ID,
    // 調査対象APIの想定外のエラーが発生した
    201299: ErrorCode.INSPECTION_PLAN_INV_INSPECTION_TARGET_UNKNOWN_ERROR,
    /* 調査結果 API */
    // 調査結果APIのバリデーションエラー
    201100: ErrorCode.INSPECTION_PLAN_INSPECTION_RESULT_VALIDATION_ERROR,
    // 調査結果APIのCORSエラー
    201101: ErrorCode.INSPECTION_PLAN_INSPECTION_RESULT_CORS_ERROR,
    // 調査結果APIの実行権限エラー
    201102: ErrorCode.INSPECTION_PLAN_INSPECTION_RESULT_NO_AUTH,
    // 調査結果APIのサポート外の関数が指定された
    201103: ErrorCode.INSPECTION_PLAN_INSPECTION_RESULT_NO_SUPPORTED_FUNCTION,
    // 調査結果APIで存在しない調査IDが指定された (sendBack)
    201111: ErrorCode.INSPECTION_PLAN_INSPECTION_RESULT_NO_INSPECTION_ID_SEND_BACK,
    // 調査結果APIで存在しない調査IDが指定された (modify)
    201112: ErrorCode.INSPECTION_PLAN_INSPECTION_RESULT_NO_INSPECTION_ID_MODIFY,
    // 調査結果APIで無効なtypeが指定された
    201113: ErrorCode.INSPECTION_PLAN_INSPECTION_RESULT_INVALID_TYPE,
    // 調査結果APIで無効なtypeが指定された
    201199: ErrorCode.INSPECTION_PLAN_INSPECTION_RESULT_UNKNOWN_ERROR,
    // 調査対象APIのバリデーションエラー
    101603: ErrorCode.INSPECTION_PLAN_OPE_INSPECTION_TARGET_VALIDATION_ERROR,
    // 調査対象APIのCORSエラー
    101601: ErrorCode.INSPECTION_PLAN_OPE_INSPECTION_TARGET_CORS_ERROR,
    // 調査結果APIの実行権限エラー
    101602: ErrorCode.INSPECTION_PLAN_OPE_INSPECTION_TARGET_NO_AUTH,
    // 調査結果APIのサポート外の関数が指定された
    101600: ErrorCode.INSPECTION_PLAN_OPE_INSPECTION_TARGET_NO_SUPPORTED_FUNCTION,
    // 調査結果APIのget_itemでエラーが発生
    101611: ErrorCode.INSPECTION_PLAN_OPE_INSPECTION_TARGET_FAILED_GET_ITEM,
    // 調査結果APIでエラーメッセージ定義ファイルの読み込みに失敗
    101698: ErrorCode.INSPECTION_PLAN_OPE_INSPECTION_TARGET_FAILED_READ_EXCEPTION_JSON,
    // 調査結果APIで想定外なエラーが発生した
    101699: ErrorCode.INSPECTION_PLAN_OPE_INSPECTION_TARGET_UNKNOWN_ERROR,
    // 申し送り事項APIのバリデーションエラー
    201800: ErrorCode.INSPECTION_PLAN_MESSAGE_FOR_SUCCESSOR_VALIDATION_ERROR,
    // 申し送り事項APIのCORSエラー
    201801: ErrorCode.INSPECTION_PLAN_MESSAGE_FOR_SUCCESSOR_CORS_ERROR,
    // 申し送り事項APIのget権限エラー
    201802: ErrorCode.INSPECTION_PLAN_MESSAGE_FOR_SUCCESSOR_NO_AUTH,
    // 申し送り事項APIでサポート外の関数が指定された
    201803: ErrorCode.INSPECTION_PLAN_MESSAGE_FOR_SUCCESSOR_NO_SUPPORTED_FUNCTION,
    // 申し送り事項APIで想定外のエラーが発生した
    201899: ErrorCode.INSPECTION_PLAN_MESSAGE_FOR_SUCCESSOR_UNKNOWN_ERROR,
    // 申請情報APIのバリデーションエラー
    101403: ErrorCode.INSPECTION_PLAN_APPLICATIONS_VALIDATION_ERROR,
    // 申請情報APIのCORSエラー
    101401: ErrorCode.INSPECTION_PLAN_APPLICATIONS_CORS_ERROR,
    // 申請情報APIの実行権限エラー
    101402: ErrorCode.INSPECTION_PLAN_APPLICATIONS_NO_AUTH,
    // 申請情報APIのサポート外の関数が指定された
    101400: ErrorCode.INSPECTION_PLAN_APPLICATIONS_NO_SUPPORTED_FUNCTION,
    // 申請情報APIのcount関数でエラー
    101420: ErrorCode.INSPECTION_PLAN_APPLICATIONS_FAILED_COUNT,
    // 申請情報APIでエラーメッセージ定義ファイルの読み込みに失敗
    101498: ErrorCode.INSPECTION_PLAN_APPLICATIONS_FAILED_READ_EXCEPTION_JSON,
    // 申請情報APIで想定外のエラーが発生した
    101499: ErrorCode.INSPECTION_PLAN_APPLICATIONS_UNKNOWN_ERROR,
    // 行政区画API デバイス認証でエラー発生またはdeviceTokenがない
    901180: ErrorCode.INSPECTION_PLAN_DEVICE_AUTH_ERROR,
    // 行政区画API 後方支援側の設定でデバイス認証が不許可
    901181: ErrorCode.INSPECTION_PLAN_DEVICE_AUTH_NO_PERMISSION_ERROR,
    // 調査対象家屋API デバイス認証でエラー発生またはdeviceTokenがない
    201280: ErrorCode.INSPECTION_PLAN_DEVICE_AUTH_ERROR,
    // 調査対象家屋API 後方支援側の設定でデバイス認証が不許可
    201281: ErrorCode.INSPECTION_PLAN_DEVICE_AUTH_NO_PERMISSION_ERROR,
    // 調査結果API デバイス認証でエラー発生またはdeviceTokenがない
    201180: ErrorCode.INSPECTION_PLAN_DEVICE_AUTH_ERROR,
    // 調査結果API 後方支援側の設定でデバイス認証が不許可
    201181: ErrorCode.INSPECTION_PLAN_DEVICE_AUTH_NO_PERMISSION_ERROR,
    // 自治体設定API デバイス認証でエラー発生またはdeviceTokenがない
    901780: ErrorCode.INSPECTION_PLAN_DEVICE_AUTH_ERROR,
    // 自治体設定API 後方支援側の設定でデバイス認証が不許可
    901781: ErrorCode.INSPECTION_PLAN_DEVICE_AUTH_NO_PERMISSION_ERROR,
    // 調査対象API デバイス認証でエラー発生またはdeviceTokenがない
    101680: ErrorCode.INSPECTION_PLAN_DEVICE_AUTH_ERROR,
    // 調査対象API 後方支援側の設定でデバイス認証が不許可
    101681: ErrorCode.INSPECTION_PLAN_DEVICE_AUTH_NO_PERMISSION_ERROR,
    // 申請情報API デバイス認証でエラー発生またはdeviceTokenがない
    101480: ErrorCode.INSPECTION_PLAN_DEVICE_AUTH_ERROR,
    // 申請情報API 後方支援側の設定でデバイス認証が不許可
    101481: ErrorCode.INSPECTION_PLAN_DEVICE_AUTH_NO_PERMISSION_ERROR,
    // 申し送り事項API デバイス認証でエラー発生またはdeviceTokenがない
    201880: ErrorCode.INSPECTION_PLAN_DEVICE_AUTH_ERROR,
    // 申し送り事項API 後方支援側の設定でデバイス認証が不許可
    201881: ErrorCode.INSPECTION_PLAN_DEVICE_AUTH_NO_PERMISSION_ERROR,
    // 状態遷移ログAPI デバイス認証でエラー発生またはdeviceTokenがない
    201580: ErrorCode.INSPECTION_PLAN_DEVICE_AUTH_ERROR,
    // 状態遷移ログAPI 後方支援側の設定でデバイス認証が不許可
    201581: ErrorCode.INSPECTION_PLAN_DEVICE_AUTH_NO_PERMISSION_ERROR,
    // 申し送り事項API デバイス認証でエラー発生またはdeviceTokenがない
    901080: ErrorCode.INSPECTION_PLAN_DEVICE_AUTH_ERROR,
    // 申し送り事項API 後方支援側の設定でデバイス認証が不許可
    901081: ErrorCode.INSPECTION_PLAN_DEVICE_AUTH_NO_PERMISSION_ERROR,
    // 申請受付API デバイス認証でエラー発生またはdeviceTokenがない
    101380: ErrorCode.INSPECTION_PLAN_DEVICE_AUTH_ERROR,
    // 申請受付API 後方支援側の設定でデバイス認証が不許可
    101381: ErrorCode.INSPECTION_PLAN_DEVICE_AUTH_NO_PERMISSION_ERROR
  }
}
